import React, { useEffect, useState } from 'react';
import { ORIGINAL_PET_ASSETS, PET_ACTION_ANIMATIONS, STAR_EGG_ANIMATIONS } from './config/evolutionAssets';
import type { PetSpriteAnimation } from './config/evolutionAssets';
import { PET_SHOP_ITEMS, PetInventory } from './types';
import type { OriginalPetForm } from './types';

/**
 * 레이어 방식 캐릭터 렌더러.
 * 현재 진화 형태 또는 커스텀 이미지 위에 장착 아이템을 쌓아 그린다.
 */

const ITEM_ASSET_BASE = '/assets/virtual-pet/cat/items';

type SlotPos = React.CSSProperties;

// 이모지 placeholder일 때만 쓰는 슬롯별 위치(% 기준).
// PNG가 들어오면 무시되고, PNG는 캔버스 전체에 미리 정렬돼 있으므로 그대로 겹쳐진다.
const PLACEHOLDER_POS: Record<'hat' | 'accessory', SlotPos> = {
  hat: { top: '-4%', left: '50%', transform: 'translateX(-50%)' },
  accessory: { top: '34%', left: '50%', transform: 'translateX(-50%)' }
};

interface LayerProps {
  src: string;
  emoji: string;
  /** 이모지 폴백 위치. 없으면 컨테이너 가득(=베이스) */
  emojiPos?: SlotPos;
  emojiSize: number;
  z: number;
  alt: string;
  pixelated?: boolean;
}

const Layer: React.FC<LayerProps> = ({ src, emoji, emojiPos, emojiSize, z, alt, pixelated = false }) => {
  const [failed, setFailed] = useState(false);

  if (!failed) {
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          imageRendering: pixelated ? 'pixelated' : undefined,
          zIndex: z,
          pointerEvents: 'none'
        }}
      />
    );
  }

  // PNG가 없을 때 이모지 placeholder
  return (
    <div
      aria-label={alt}
      style={{
        position: 'absolute',
        ...(emojiPos ?? { inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        fontSize: emojiSize,
        lineHeight: 1,
        zIndex: z,
        pointerEvents: 'none',
        userSelect: 'none'
      }}
    >
      {emoji}
    </div>
  );
};

interface SpriteSheetLayerProps {
  animation: PetSpriteAnimation;
  fallbackSrc: string;
  petName: string;
}

const SpriteSheetLayer: React.FC<SpriteSheetLayerProps> = ({ animation, fallbackSrc, petName }) => {
  const [failed, setFailed] = useState(false);
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    setFailed(false);
    setFrame(0);
    if (animation.frameCount <= 1) return;

    const frameDuration = animation.durationMs / animation.frameCount;
    const timer = window.setInterval(() => {
      setFrame((current) => {
        if (current < animation.frameCount - 1) return current + 1;
        if (animation.loop) return 0;
        window.clearInterval(timer);
        return current;
      });
    }, frameDuration);

    return () => window.clearInterval(timer);
  }, [animation.src, animation.frameCount, animation.durationMs, animation.loop]);

  if (failed) {
    return <Layer src={fallbackSrc} emoji="✨" emojiSize={160} z={1} alt={petName} pixelated />;
  }

  const position = animation.frameCount > 1 ? (frame / (animation.frameCount - 1)) * 100 : 0;

  return (
    <div
      role="img"
      aria-label={petName}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        backgroundImage: `url(${animation.src})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${animation.frameCount * 100}% 100%`,
        backgroundPosition: `${position}% 0%`,
        imageRendering: 'pixelated'
      }}
    >
      <img src={animation.src} alt="" onError={() => setFailed(true)} style={{ display: 'none' }} />
    </div>
  );
};

interface LayeredPetProps {
  /** 캐릭터 한 변 크기(px) */
  size: number;
  equipped: PetInventory['equipped'];
  petForm: OriginalPetForm;
  petName?: string;
  isEating?: boolean;
  isPlaying?: boolean;
  isSleeping?: boolean;
  isEvolving?: boolean;
  isReadyToEvolve?: boolean;
}

const findItem = (id?: string) => (id ? PET_SHOP_ITEMS.find((it) => it.id === id) : undefined);

const LayeredPet: React.FC<LayeredPetProps> = ({
  size,
  equipped,
  petForm,
  petName = '캐릭터',
  isEating = false,
  isPlaying = false,
  isSleeping = false,
  isEvolving = false,
  isReadyToEvolve = false
}) => {
  const hat = findItem(equipped.hat);
  const accessory = findItem(equipped.accessory);
  const isEgg = petForm === 'starEgg';
  const asset = ORIGINAL_PET_ASSETS[petForm];
  const actionAnimations = PET_ACTION_ANIMATIONS[petForm];
  const eggAnimation = isEvolving
    ? STAR_EGG_ANIMATIONS.hatch
    : isReadyToEvolve
      ? STAR_EGG_ANIMATIONS.ready
      : isSleeping
        ? STAR_EGG_ANIMATIONS.sleep
        : isPlaying
          ? STAR_EGG_ANIMATIONS.shake
          : isEating
            ? STAR_EGG_ANIMATIONS.touch
            : STAR_EGG_ANIMATIONS.idle;
  const spriteAnimation: PetSpriteAnimation = isEgg
    ? eggAnimation
    : isSleeping && actionAnimations?.sleep
      ? actionAnimations.sleep
      : isPlaying && actionAnimations?.play
        ? actionAnimations.play
        : isEating && actionAnimations?.eat
          ? actionAnimations.eat
          : {
              src: asset.idleSheet || asset.image,
              frameCount: asset.idleFrameCount || 1,
              durationMs: 2800,
              loop: true
            };
  const animation = isSleeping
    ? 'none'
    : isEgg || (isEating && actionAnimations?.eat) || (isPlaying && actionAnimations?.play)
      ? 'none'
      : 'pet-bob 2.4s ease-in-out infinite';

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        animation
      }}
    >
      <style>{`
        @keyframes pet-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3%); } }
        @keyframes hatch-flash { 0%, 58% { opacity: 0; transform: scale(.3); } 82% { opacity: .95; } 100% { opacity: 0; transform: scale(1.8); } }
        @keyframes hatch-star-burst { 0% { opacity: 0; transform: translate(-50%, -50%) scale(.2) rotate(0); } 55% { opacity: 0; } 75% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--star-x), var(--star-y)) scale(1.15) rotate(160deg); } }
      `}</style>

      {/* 현재 진화 형태 */}
      <SpriteSheetLayer animation={spriteAnimation} fallbackSrc={asset.image} petName={petName} />

      {isEgg && isEvolving && (
        <>
          <div style={{ position: 'absolute', inset: '10%', borderRadius: '50%', background: 'radial-gradient(circle, white 0%, #FFF2A6 38%, transparent 72%)', zIndex: 5, pointerEvents: 'none', animation: 'hatch-flash 1.7s ease-out both' }} />
          {[
            ['-80px', '-72px'], ['82px', '-58px'], ['-92px', '30px'], ['88px', '42px'], ['-30px', '-105px'], ['35px', '95px']
          ].map(([x, y], index) => (
            <span
              key={`${x}-${y}`}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                zIndex: 6,
                color: index % 2 ? '#FFF7B2' : '#FFD45C',
                fontSize: Math.max(14, Math.round(size * 0.09)),
                lineHeight: 1,
                pointerEvents: 'none',
                '--star-x': x,
                '--star-y': y,
                animation: `hatch-star-burst 1.7s ease-out ${index * 0.04}s both`
              } as React.CSSProperties}
            >
              ✦
            </span>
          ))}
        </>
      )}

      {/* 액세서리 (안경·리본 등) */}
      {accessory && (
        <Layer
          src={`${ITEM_ASSET_BASE}/${accessory.id}.png`}
          emoji={accessory.image}
          emojiPos={PLACEHOLDER_POS.accessory}
          emojiSize={Math.round(size * 0.3)}
          z={2}
          alt={accessory.name}
        />
      )}

      {/* 모자 */}
      {hat && (
        <Layer
          src={`${ITEM_ASSET_BASE}/${hat.id}.png`}
          emoji={hat.image}
          emojiPos={PLACEHOLDER_POS.hat}
          emojiSize={Math.round(size * 0.38)}
          z={3}
          alt={hat.name}
        />
      )}
    </div>
  );
};

export default LayeredPet;
